// Information architecture (7.5). Primary navigation is about product jobs;
// the internal hierarchy (Client → Brand → Campaign → Creative → Version →
// Distribution → Tag) appears contextually, and explicit management lives in
// a secondary "Gestión" group shown when it is useful to the user.
import {
  BarChart3,
  Building2,
  CreditCard,
  FileClock,
  FileText,
  LayoutDashboard,
  Megaphone,
  Palette,
  Settings,
  type LucideIcon,
} from "lucide-react";
import type { FrontDesk } from "@/api/types";

export interface NavItem {
  readonly label: string;
  readonly to: string;
  readonly icon: LucideIcon;
  readonly soon?: boolean;
}

export interface NavGroup {
  readonly label: string | null;
  readonly items: NavItem[];
}

/** Commercial capabilities of the organization (ADR-0047), as the server returned them. */
export interface CommercialNav {
  readonly purchaseOrdersEnabled: boolean;
}

export function navigation(orgId: string, fd: FrontDesk | undefined, commercial?: CommercialNav | null): NavGroup[] {
  const base = `/o/${orgId}`;
  const management: NavItem[] = [];
  // Explicit hierarchy management: for those who can manage it or already work with several clients.
  if (fd && (fd.capabilities.manageClients || fd.hierarchy.clients > 1))
    management.push({ label: "Clientes y marcas", to: `${base}/clients`, icon: Building2 });
  if (fd?.capabilities.readAudit) management.push({ label: "Auditoría", to: `${base}/audit`, icon: FileClock });
  return [
    {
      label: null,
      items: [
        { label: "Front Desk", to: base, icon: LayoutDashboard },
        { label: "Creatividades", to: `${base}/creatives`, icon: Palette },
        { label: "Campañas", to: `${base}/campaigns`, icon: Megaphone },
        { label: "Analítica", to: `${base}/analytics`, icon: BarChart3 },
        ...(commercial?.purchaseOrdersEnabled
          ? [{ label: "Órdenes de compra", to: `${base}/purchase-orders`, icon: FileText }]
          : []),
        { label: "Facturación", to: `${base}/billing`, icon: CreditCard, soon: true },
      ],
    },
    ...(management.length > 0 ? [{ label: "Gestión", items: management }] : []),
    { label: null, items: [{ label: "Configuración", to: `${base}/settings`, icon: Settings }] },
  ];
}
